import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { L } from "@/constants/labels";
import { ChevronLeft, ChevronRight, Save, Trash2, X, Copy, ExternalLink, RefreshCw } from "lucide-react";

const MONTH_NAMES = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const DAY_LABELS = ["L", "M", "M", "J", "V", "S", "D"];

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n, 1); x.setHours(0, 0, 0, 0); return x; };
const mondayOf = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const isoWeekNum = (d) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = (t.getUTCDay() + 6) % 7; t.setUTCDate(t.getUTCDate() - day + 3);
  const first = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((t - first) / 86400000 - 3 + ((first.getUTCDay() + 6) % 7)) / 7);
};

// ---------- Page ----------
export default function MonCalendrier() {
  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
  const firstMonth = useMemo(() => { const d = new Date(today); d.setDate(1); return d; }, [today]);
  const lastMonth = useMemo(() => addMonths(firstMonth, 11), [firstMonth]);
  const [cursor, setCursor] = useState(firstMonth);
  const [periodes, setPeriodes] = useState([]); // [{id, date_debut, date_fin, type, libelle}]
  const [editing, setEditing] = useState(null); // period being edited or draft
  const [savingCount, setSavingCount] = useState(0);
  const [pending, setPending] = useState(null); // batch to flush
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  const [ical, setIcal] = useState(null);
  const flushTimer = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const on = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  const load = useCallback(async () => {
    try {
      const from = toISO(firstMonth);
      const to = toISO(new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0));
      const { data } = await api.get(`/periodes/me?from_=${from}&to=${to}`);
      setPeriodes(data || []);
    } catch (err) { toast.error(fmtError(err)); }
  }, [firstMonth, lastMonth]);
  useEffect(() => { load(); }, [load]);

  // Cartographie jour ISO -> période active (dernière période gagne — motif lisible)
  const dayMap = useMemo(() => {
    const m = {};
    for (const p of periodes) {
      const d1 = parseISO(p.date_debut), d2 = parseISO(p.date_fin);
      for (let d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) {
        m[toISO(d)] = p;
      }
    }
    return m;
  }, [periodes]);

  // ---------- Save flow ----------
  const saveNow = useCallback(async (draft) => {
    if (!draft) return;
    setSavingCount((n) => n + 1);
    try {
      if (draft._delete) {
        await api.delete(`/periodes/${draft.id}`);
      } else if (draft.id) {
        await api.patch(`/periodes/${draft.id}`, {
          date_debut: draft.date_debut, date_fin: draft.date_fin,
          type: draft.type, libelle: draft.libelle,
        });
      } else {
        await api.post("/periodes", {
          date_debut: draft.date_debut, date_fin: draft.date_fin,
          type: draft.type, libelle: draft.libelle || null,
        });
      }
      await load();
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setSavingCount((n) => n - 1);
    }
  }, [load]);

  // Flush débounce sur "pending" (draft depuis drag/click), 350ms
  useEffect(() => {
    if (!pending) return;
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(() => {
      saveNow(pending);
      setPending(null);
    }, 350);
    return () => { if (flushTimer.current) clearTimeout(flushTimer.current); };
  }, [pending, saveNow]);

  // beforeunload : flush synchrone si en attente
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (pending || savingCount > 0) {
        // Note : sendBeacon serait plus fiable mais l'API POST/PATCH n'est pas
        // supportée en fetch-keepalive de manière stable côté Safari. On
        // affiche donc au minimum le prompt navigateur pour laisser à l'user
        // le temps de re-cliquer sur "Enregistrer" ou d'attendre le flush.
        e.preventDefault(); e.returnValue = "";
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden" && pending) {
        // Best-effort flush immédiat quand l'onglet passe en arrière-plan
        if (flushTimer.current) clearTimeout(flushTimer.current);
        saveNow(pending); setPending(null);
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [pending, savingCount, saveNow]);

  // ---------- Interactions ----------
  const openDayEditor = (iso) => {
    if (parseISO(iso) < today) return;
    const existing = dayMap[iso];
    if (existing) {
      setEditing({ ...existing });
    } else {
      setEditing({ date_debut: iso, date_fin: iso, type: "indisponible", libelle: "" });
    }
  };
  const applyBlockDrag = (isoStart, isoEnd) => {
    const [d1, d2] = [isoStart, isoEnd].sort();
    setEditing({ date_debut: d1, date_fin: d2, type: "indisponible", libelle: "" });
  };
  const onDayMouseDown = (iso) => (ev) => {
    if (ev.button !== 0) return;
    if (parseISO(iso) < today) return;
    ev.preventDefault();
    dragRef.current = { start: iso, current: iso };
  };
  const onDayMouseEnter = (iso) => () => {
    if (!dragRef.current) return;
    if (parseISO(iso) < today) return;
    dragRef.current.current = iso;
  };
  useEffect(() => {
    const up = () => {
      if (dragRef.current) {
        const { start, current } = dragRef.current;
        if (start === current) openDayEditor(start);
        else applyBlockDrag(start, current);
        dragRef.current = null;
      }
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayMap]);

  const shortcutWeek = (mondayISO) => {
    // Raccourci UI : ouvre l'éditeur pré-rempli avec Mon–Fri de cette semaine
    // en indisponible, aucun effet sur le modèle tant que l'utilisateur n'a
    // pas validé.
    const monday = parseISO(mondayISO);
    const friday = new Date(monday); friday.setDate(monday.getDate() + 4);
    setEditing({ date_debut: mondayISO, date_fin: toISO(friday), type: "indisponible", libelle: "" });
  };

  const nbSemainesDispo = useMemo(() => {
    const indispos = new Set();
    for (const p of periodes) {
      if (p.type !== "indisponible") continue;
      const d1 = parseISO(p.date_debut), d2 = parseISO(p.date_fin);
      for (let d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) indispos.add(toISO(d));
    }
    // Compte les lundis distincts qui ont au moins 1 jour Mon-Fri hors indispo
    const from = firstMonth; const to = lastMonth;
    const weeks = new Set();
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      if (d.getDay() >= 1 && d.getDay() <= 5 && !indispos.has(toISO(d))) {
        weeks.add(toISO(mondayOf(d)));
      }
    }
    return weeks.size;
  }, [periodes, firstMonth, lastMonth]);

  // ical
  const loadIcal = async () => {
    try {
      const { data } = await api.get("/ical/token/me");
      setIcal(data);
    } catch (err) { toast.error(fmtError(err)); }
  };
  useEffect(() => { loadIcal(); }, []);
  const rotateIcal = async () => {
    if (!window.confirm("Régénérer le lien iCal ? L'ancien lien cessera de fonctionner immédiatement.")) return;
    try {
      const { data } = await api.post("/ical/token/rotate", {});
      setIcal(data); toast.success("Lien iCal renouvelé");
    } catch (err) { toast.error(fmtError(err)); }
  };

  const canPrev = cursor > firstMonth;
  const canNext = cursor < lastMonth;

  return (
    <div className="max-w-5xl" data-testid="mon-calendrier-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">Mon calendrier</h1>
          <p className="text-sm text-[#3E4C59] mt-1">
            Cliquez un jour ou glissez pour créer une période. Cliquez sur une période existante pour l'éditer.
            {L.wording.calendrierJoursNonRenseignes}
          </p>
        </div>
        <div className="card px-3 py-2 text-sm" data-testid="week-counter">
          <div className="text-[#3E4C59] text-xs">Semaines disponibles (12 mois)</div>
          <div className="text-2xl font-bold text-[#1B3A5C]">{nbSemainesDispo}</div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <button onClick={() => canPrev && setCursor(addMonths(cursor, -1))} disabled={!canPrev} className="btn-outline" data-testid="cal-prev">
          <ChevronLeft size={16} /> Mois précédent
        </button>
        <div className="text-lg font-semibold text-[#1F2933]" data-testid="cal-title">
          {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
        </div>
        <button onClick={() => canNext && setCursor(addMonths(cursor, 1))} disabled={!canNext} className="btn-outline" data-testid="cal-next">
          Mois suivant <ChevronRight size={16} />
        </button>
      </div>

      <div className="mt-2 flex items-center gap-3 text-xs text-[#3E4C59] flex-wrap">
        <Legend swatch="bg-[#C0392B]" label="Indisponible" />
        <Legend swatch="bg-[#2E7D32]" label="Disponible (déclaré)" />
        <Legend swatch="bg-white border" label="Non renseigné (éligible, à confirmer)" />
        {savingCount > 0 && (
          <span className="text-[#E8710A] inline-flex items-center gap-1" data-testid="save-indicator">
            <Save size={12} /> Enregistrement…
          </span>
        )}
      </div>

      <div className="mt-4">
        {isMobile
          ? <WeekList cursor={cursor} dayMap={dayMap} openEditor={openDayEditor} onWeekShortcut={shortcutWeek} today={today} />
          : <MonthGrid cursor={cursor} dayMap={dayMap} onDayMouseDown={onDayMouseDown}
                        onDayMouseEnter={onDayMouseEnter} onWeekShortcut={shortcutWeek} today={today} />}
      </div>

      {ical && (
        <div className="mt-6 card p-4">
          <h2 className="font-semibold text-[#1F2933] mb-2">Export iCal (Google Agenda / Outlook)</h2>
          <p className="text-xs text-[#3E4C59] mb-2">
            Lien lecture seule, token opaque distinct de votre mot de passe. Vous pouvez le renouveler à tout moment.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <input readOnly value={ical.ical_url} className="input font-mono text-xs flex-1" data-testid="ical-url" />
            <button onClick={() => { navigator.clipboard.writeText(ical.ical_url); toast.success("URL copiée"); }} className="btn-outline text-xs" data-testid="ical-copy">
              <Copy size={12} /> Copier
            </button>
            <a href={ical.ical_url} target="_blank" rel="noreferrer" className="btn-outline text-xs" data-testid="ical-open">
              <ExternalLink size={12} /> Ouvrir
            </a>
            <button onClick={rotateIcal} className="btn-outline text-xs" data-testid="ical-rotate">
              <RefreshCw size={12} /> Régénérer
            </button>
          </div>
        </div>
      )}

      {editing && (
        <EditorModal draft={editing} onClose={() => setEditing(null)}
          onSave={async (d) => { await saveNow(d); setEditing(null); }}
          onDelete={async () => { await saveNow({ ...editing, _delete: true }); setEditing(null); }}
        />
      )}
    </div>
  );
}

function Legend({ swatch, label }) {
  return <span className="inline-flex items-center gap-1"><span className={`inline-block w-3 h-3 rounded ${swatch}`} /> {label}</span>;
}

// ---------- Editor modal ----------
function EditorModal({ draft, onClose, onSave, onDelete }) {
  const [d1, setD1] = useState(draft.date_debut);
  const [d2, setD2] = useState(draft.date_fin);
  const [type, setType] = useState(draft.type || "indisponible");
  const [libelle, setLibelle] = useState(draft.libelle || "");
  const isEdit = !!draft.id;
  const submit = async (e) => {
    e.preventDefault();
    if (d2 < d1) { toast.error("La date de fin doit être ≥ date de début"); return; }
    await onSave({ ...draft, date_debut: d1, date_fin: d2, type, libelle });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="periode-editor">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-[6px] shadow-lg max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-[#E4E7EB]">
          <h2 className="font-semibold text-[#1F2933]">{isEdit ? "Modifier la période" : "Nouvelle période"}</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Début</label>
              <input type="date" required className="input" value={d1} onChange={(e) => setD1(e.target.value)} data-testid="periode-date-debut" /></div>
            <div><label className="label">Fin</label>
              <input type="date" required className="input" value={d2} onChange={(e) => setD2(e.target.value)} data-testid="periode-date-fin" /></div>
          </div>
          <div><label className="label">Type</label>
            <div className="flex gap-2">
              {[["indisponible", "Indisponible"], ["disponible", "Disponible"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setType(v)}
                  className={`flex-1 px-3 py-2 rounded border text-sm ${type === v
                    ? (v === "indisponible" ? "bg-[#C0392B] text-white border-[#C0392B]" : "bg-[#2E7D32] text-white border-[#2E7D32]")
                    : "bg-white text-[#1F2933] border-[#CBD5E1]"}`}
                  data-testid={`periode-type-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <div><label className="label">Motif <span className="text-[#3E4C59] font-normal">(facultatif — congés, autre chantier…)</span></label>
            <input className="input" value={libelle} onChange={(e) => setLibelle(e.target.value)} maxLength={200} data-testid="periode-libelle" placeholder="Ex. Congés, Chantier Villa Dupont" /></div>
          <div className="flex justify-between gap-2 pt-2 border-t border-[#E4E7EB]">
            <div>{isEdit && <button type="button" onClick={onDelete} className="btn-danger text-xs" data-testid="periode-delete"><Trash2 size={12} /> Supprimer</button>}</div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn-outline">Annuler</button>
              <button type="submit" className="btn-primary" data-testid="periode-save">{isEdit ? "Enregistrer" : "Créer"}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- Desktop grid ----------
function MonthGrid({ cursor, dayMap, onDayMouseDown, onDayMouseEnter, onWeekShortcut, today }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monday = mondayOf(first);
  const rows = Array.from({ length: 6 }, (_, w) => {
    const rm = new Date(monday); rm.setDate(monday.getDate() + w * 7);
    return { monday: rm, days: Array.from({ length: 7 }, (_, i) => { const d = new Date(rm); d.setDate(rm.getDate() + i); return d; }) };
  });
  return (
    <div className="card overflow-hidden select-none">
      <div className="grid grid-cols-[80px_repeat(7,1fr)] bg-[#F5F7FA] border-b border-[#E4E7EB] text-xs font-semibold">
        <div className="p-2 text-[#3E4C59]">Sem.</div>
        {DAY_LABELS.map((d, i) => <div key={i} className={`p-2 text-center ${i >= 5 ? "text-[#3E4C59]" : "text-[#1F2933]"}`}>{d}</div>)}
      </div>
      {rows.filter(r => r.days.some(d => d.getMonth() === cursor.getMonth())).map(row => {
        const monISO = toISO(row.monday);
        return (
          <div key={monISO} className="grid grid-cols-[80px_repeat(7,1fr)] border-b border-[#E4E7EB] last:border-b-0">
            <button type="button" onClick={() => onWeekShortcut(monISO)}
              className="p-2 text-left text-xs font-semibold text-[#1B3A5C] hover:bg-[#F5F7FA]"
              data-testid={`cal-week-${monISO}`}
              title="Raccourci : ouvrir l'éditeur pour toute la semaine">
              S{isoWeekNum(row.monday)}
            </button>
            {row.days.map((d, i) => {
              const iso = toISO(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const isPast = d < today;
              const p = dayMap[iso];
              const bg = p?.type === "indisponible" ? "bg-[#C0392B] text-white"
                        : p?.type === "disponible" ? "bg-[#2E7D32] text-white"
                        : "bg-white text-[#1F2933]";
              return (
                <button key={iso} type="button" disabled={isPast}
                  onMouseDown={isPast ? undefined : onDayMouseDown(iso)}
                  onMouseEnter={isPast ? undefined : onDayMouseEnter(iso)}
                  className={`relative h-16 flex flex-col items-center justify-center border-l border-[#E4E7EB] transition
                    ${inMonth ? "" : "opacity-30"} ${isPast ? "cursor-not-allowed" : "cursor-pointer hover:brightness-95"}
                    ${bg} ${i >= 5 ? "opacity-70" : ""}`}
                  data-testid={`cal-day-${iso}`} data-status={p?.type || "non_renseigne"}
                  title={p?.libelle || (p?.type || "non renseigné")}>
                  <div className="text-sm font-semibold">{d.getDate()}</div>
                  {p?.libelle && <div className="text-[9px] truncate max-w-[80%]">{p.libelle}</div>}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ---------- Mobile week list ----------
function WeekList({ cursor, dayMap, openEditor, onWeekShortcut, today }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monday = mondayOf(first);
  const rows = Array.from({ length: 6 }, (_, w) => {
    const rm = new Date(monday); rm.setDate(monday.getDate() + w * 7);
    return { monday: rm, days: Array.from({ length: 5 }, (_, i) => { const d = new Date(rm); d.setDate(rm.getDate() + i); return d; }) };
  }).filter(row => row.days.some(d => d.getMonth() === cursor.getMonth()));
  return (
    <div className="space-y-2">
      {rows.map(row => (
        <div key={toISO(row.monday)} className="card p-3">
          <button onClick={() => onWeekShortcut(toISO(row.monday))} className="text-sm font-semibold text-[#1B3A5C]"
            data-testid={`cal-week-${toISO(row.monday)}`}>
            Semaine {isoWeekNum(row.monday)} — du {row.monday.getDate()}/{row.monday.getMonth() + 1}
          </button>
          <div className="grid grid-cols-5 gap-1 mt-2">
            {row.days.map(d => {
              const iso = toISO(d);
              const p = dayMap[iso];
              const bg = p?.type === "indisponible" ? "bg-[#C0392B] text-white"
                        : p?.type === "disponible" ? "bg-[#2E7D32] text-white"
                        : "bg-white text-[#1F2933] border";
              return (
                <button key={iso} disabled={d < today} onClick={() => openEditor(iso)}
                  className={`h-14 flex flex-col items-center justify-center rounded ${bg} ${d < today ? "opacity-40" : ""}`}
                  data-testid={`cal-day-${iso}`} data-status={p?.type || "non_renseigne"}>
                  <div className="text-xs">{["L", "M", "M", "J", "V"][d.getDay() ? d.getDay() - 1 : 0]}</div>
                  <div className="text-sm font-semibold">{d.getDate()}</div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
